import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import {
  getBracketOptions, generateBracketSeeding, getDefaultStageConfig,
  getStageName, getBracketRounds, buildBracketView
} from "../utils/brackets";
import CodeQR from "./CodeQR";

/**
 * EliminationSetup — shown when admin is ready to advance from pools to elimination.
 * Lets them pick bracket size, see seeded matchups, adjust, and confirm.
 */
export function EliminationSetup({ standings, onConfirm, onCancel }) {
  const totalFighters = standings.length;
  const bracketOptions = getBracketOptions(totalFighters);
  const recommended = bracketOptions.find(o => o.recommended) || bracketOptions[bracketOptions.length - 1];
  
  const [bracketSize, setBracketSize] = useState(recommended?.size || 8);
  const [matchups, setMatchups] = useState(null);
  const [stageConfig, setStageConfig] = useState(null);
  const [swapFrom, setSwapFrom] = useState(null);

  const advancing = standings.slice(0, bracketSize);
  const eliminated = standings.slice(bracketSize);

  const actualBracketSize = Math.pow(2, Math.ceil(Math.log2(Math.max(2, bracketSize))));

  const handleGenerate = () => {
    const seeded = generateBracketSeeding(advancing, bracketSize);
    setMatchups(seeded);
    setStageConfig(getDefaultStageConfig(actualBracketSize));
  };

  const handleSwap = (matchIdx, position) => {
    if (swapFrom === null) {
      setSwapFrom({ matchIdx, position });
    } else {
      // Swap the two fighters
      const updated = matchups.map(m => ({ ...m, fighter1: { ...m.fighter1 }, fighter2: { ...m.fighter2 } }));
      const fromMatch = updated[swapFrom.matchIdx];
      const toMatch = updated[matchIdx];
      
      const fromFighter = swapFrom.position === 1 ? fromMatch.fighter1 : fromMatch.fighter2;
      const toFighter = position === 1 ? toMatch.fighter1 : toMatch.fighter2;
      
      if (swapFrom.position === 1) fromMatch.fighter1 = toFighter;
      else fromMatch.fighter2 = toFighter;
      if (position === 1) toMatch.fighter1 = fromFighter;
      else toMatch.fighter2 = fromFighter;
      
      setMatchups(updated);
      setSwapFrom(null);
    }
  };

  const updateStageRounds = (stageIdx, rounds) => {
    setStageConfig(stageConfig.map((s, i) => i === stageIdx ? { ...s, roundsPerBout: rounds } : s));
  };

  const updateStagePhase = (stageIdx, phase) => {
    setStageConfig(stageConfig.map((s, i) => i === stageIdx ? { ...s, phase } : s));
  };

  return (
    <div className="mb-5">
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-4">
        <div className="font-display text-xs font-bold text-yellow-500 tracking-widest mb-3">ADVANCE TO ELIMINATION</div>

        {!matchups ? (
          <>
            {/* Bracket size selection */}
            <div className="mb-4">
              <label htmlFor="bracket-advance-count" className="text-xs font-bold text-gray-500 tracking-wider block mb-2">HOW MANY ADVANCE?</label>
              <div className="flex items-center gap-3 mb-3">
                <input id="bracket-advance-count" type="number" value={bracketSize} onChange={(e) => setBracketSize(Math.max(2, Math.min(totalFighters, parseInt(e.target.value) || 2)))}
                  min={2} max={totalFighters}
                  className="w-20 p-2 bg-dark-800 border border-dark-500 rounded-lg text-white text-center font-display text-xl font-extrabold outline-none focus:border-gray-400" />
                <span className="text-gray-500 text-sm">of {totalFighters} fighters ({Math.round((bracketSize / totalFighters) * 100)}%)</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {bracketOptions.map(opt => (
                  <button key={opt.size} onClick={() => setBracketSize(opt.size)} aria-pressed={bracketSize === opt.size}
                    className="px-3 py-1.5 rounded-lg text-xs font-display font-bold border cursor-pointer"
                    style={{
                      background: bracketSize === opt.size ? "#388E3C22" : "#111",
                      borderColor: bracketSize === opt.size ? "#4CAF50" : "#333",
                      color: bracketSize === opt.size ? "#fff" : "#bbb",
                    }}>
                    {opt.size} ({opt.percentage}%){opt.recommended ? " ★" : ""}{bracketSize === opt.size ? " ✓" : ""}
                  </button>
                ))}
              </div>
              {![2,4,8,16,32,64].includes(bracketSize) && (
                <p className="text-yellow-600 text-[10px] mt-2">Note: bracket size {bracketSize} is not a power of 2. The bracket will be padded with BYEs to the next power of 2 ({Math.pow(2, Math.ceil(Math.log2(bracketSize)))}).</p>
              )}
            </div>

            {/* Preview who advances — full standings with dynamic cutoff */}
            <div className="mb-4">
              <div className="text-xs font-bold text-gray-500 mb-2">STANDINGS</div>
              <div className="bg-dark-800 rounded-xl overflow-x-auto">
                <table className="w-full min-w-[250px] text-xs">
                  <caption className="sr-only">Standings for elimination bracket selection</caption>
                  <thead><tr className="border-b border-dark-500 text-gray-300">
                    <th scope="col" className="p-2 text-left">Rank</th>
                    <th scope="col" className="p-2 text-left">Fighter</th>
                    <th scope="col" className="p-2 text-center">P/I</th>
                    <th scope="col" className="p-2 text-center">P/R</th>
                  </tr></thead>
                  <tbody>
                {standings.map((s, i) => {
                  const isAdvancing = i < bracketSize;
                  const isCutoff = i === bracketSize - 1 && i < standings.length - 1;
                  return (
                    <Fragment key={s.name}>
                      <tr className="border-b border-dark-500/20" style={{ background: isAdvancing ? "#1a2a1a" : undefined }}>
                        <td className="p-2 font-display font-extrabold" style={{ color: isAdvancing ? "#81C784" : "#bbb" }}>{i + 1}</td>
                        <th scope="row" className="p-2 text-left font-semibold text-gray-200">{s.name}</th>
                        <td className="p-2 text-center font-display font-bold text-gray-200">{s.normalized ? s.PI.toFixed(1) : s.PI}</td>
                        <td className="p-2 text-center font-display font-bold text-gray-200">{s.normalized ? s.PR.toFixed(1) : s.PR}</td>
                      </tr>
                      {isCutoff && (
                        <tr><td colSpan={4} className="p-2 text-center font-bold text-green-action-text bg-[#1a2a1a]">▲ ADVANCING ({bracketSize}) — ELIMINATED BELOW ▼</td></tr>
                      )}
                    </Fragment>
                  );
                })}
                  </tbody>
                </table>
              </div>
            </div>

            <button onClick={handleGenerate}
              className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide border-none cursor-pointer">
              Generate Bracket →
            </button>
          </>
        ) : (
          <>
            {/* Stage configuration */}
            <div className="mb-4">
              <div className="text-xs font-bold text-gray-500 tracking-wider mb-2">STAGE CONFIGURATION</div>
              {stageConfig.map((stage, i) => (
                <div key={i} className="py-3 border-b border-dark-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-semibold text-gray-300">{stage.stageName}</span>
                      <span className="text-xs text-gray-600 ml-2">({stage.boutsInStage} bout{stage.boutsInStage > 1 ? "s" : ""})</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-[10px] text-gray-600 mb-1">Rounds</div>
                      <div className="flex gap-1">
                        {[1, 2, 3].map(n => (
                          <button key={n} onClick={() => updateStageRounds(i, n)} aria-pressed={stage.roundsPerBout === n} aria-label={`${n} rounds for ${stage.stageName}`}
                            className="min-w-8 h-8 px-1 rounded-lg font-display text-sm font-extrabold border cursor-pointer"
                            style={{
                              background: stage.roundsPerBout === n ? "#388E3C22" : "#111",
                              borderColor: stage.roundsPerBout === n ? "#81C784" : "#666",
                              color: stage.roundsPerBout === n ? "#fff" : "#bbb",
                            }}>
                            {n}{stage.roundsPerBout === n ? " ✓" : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-600 mb-1">Scale</div>
                      <div className="flex gap-1">
                        {[{ key: "elimination", label: "3-pt" }, { key: "pool", label: "5-pt" }].map(opt => (
                          <button key={opt.key} onClick={() => updateStagePhase(i, opt.key)} aria-pressed={(stage.phase || "elimination") === opt.key} aria-label={`${opt.label} scale for ${stage.stageName}`}
                            className="px-3 h-8 rounded-lg text-xs font-display font-bold border cursor-pointer"
                            style={{
                              background: (stage.phase || "elimination") === opt.key ? "#388E3C22" : "#111",
                              borderColor: (stage.phase || "elimination") === opt.key ? "#81C784" : "#666",
                              color: (stage.phase || "elimination") === opt.key ? "#fff" : "#bbb",
                            }}>
                            {opt.label}{(stage.phase || "elimination") === opt.key ? " ✓" : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Matchups — tap to swap */}
            <div className="mb-4">
              <div className="text-xs font-bold text-gray-500 tracking-wider mb-1">FIRST ROUND MATCHUPS</div>
              <p className="text-gray-600 text-[10px] mb-3">Tap two fighters to swap their positions.</p>
              {matchups.map((m, idx) => (
                <div key={idx} className="bg-dark-800 rounded-lg p-2.5 mb-1.5 flex items-center gap-2">
                  <span className="text-gray-700 text-xs font-display font-bold w-5">{idx + 1}</span>
                  <button onClick={() => handleSwap(idx, 1)} aria-pressed={swapFrom?.matchIdx === idx && swapFrom?.position === 1} aria-label={`Swap ${m.fighter1.name} from matchup ${idx + 1}`}
                    className="flex-1 p-2 rounded-lg text-sm font-semibold text-left border cursor-pointer"
                    style={{
                      background: swapFrom?.matchIdx === idx && swapFrom?.position === 1 ? "#1565C033" : "transparent",
                      borderColor: swapFrom?.matchIdx === idx && swapFrom?.position === 1 ? "#1E88E5" : "#333",
                      color: m.fighter1.isBye ? "#bbb" : "#64B5F6",
                    }}>
                    <span className="text-[10px] text-gray-600 mr-1">({m.seed1})</span>
                    {m.fighter1.name}{swapFrom?.matchIdx === idx && swapFrom?.position === 1 ? " ✓ Selected" : ""}
                  </button>
                  <span className="text-gray-700 text-xs">vs</span>
                  <button onClick={() => handleSwap(idx, 2)} aria-pressed={swapFrom?.matchIdx === idx && swapFrom?.position === 2} aria-label={`Swap ${m.fighter2.name} from matchup ${idx + 1}`}
                    className="flex-1 p-2 rounded-lg text-sm font-semibold text-left border cursor-pointer"
                    style={{
                      background: swapFrom?.matchIdx === idx && swapFrom?.position === 2 ? "#D32F2F33" : "transparent",
                      borderColor: swapFrom?.matchIdx === idx && swapFrom?.position === 2 ? "#E53935" : "#333",
                      color: m.fighter2.isBye ? "#bbb" : "#EF9A9A",
                    }}>
                    <span className="text-[10px] text-gray-600 mr-1">({m.seed2})</span>
                    {m.fighter2.name}{swapFrom?.matchIdx === idx && swapFrom?.position === 2 ? " ✓ Selected" : ""}
                  </button>
                </div>
              ))}
              {swapFrom && (
                <button onClick={() => setSwapFrom(null)} className="text-xs text-gray-600 bg-transparent border-none cursor-pointer mt-1 hover:text-gray-400">
                  Cancel swap
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setMatchups(null); setStageConfig(null); }}
                className="flex-1 p-3 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">
                ← Back
              </button>
              <button onClick={() => onConfirm(actualBracketSize, matchups, stageConfig)}
                className="flex-1 p-3 bg-green-action-light text-white rounded-lg text-sm font-display font-bold tracking-wide border-none cursor-pointer">
                Confirm & Create Bouts
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * BracketView — displays the elimination bracket with results.
 */
export function BracketView({ bracketSize, bouts, onBoutClick, officials, officialRoles, onUpdateBout, eventId }) {
  const stages = useMemo(() => {
    if (!bracketSize || !bouts) return [];
    const elimBouts = bouts.filter(b => b.eliminationRound != null);
    
    // Reconstruct first round matchups from bouts
    const firstRoundBouts = elimBouts.filter(b => b.eliminationRound === 0)
      .sort((a, b) => a.eliminationMatch - b.eliminationMatch);
    
    const firstRoundMatchups = firstRoundBouts.map((bout, i) => ({
      matchIndex: i,
      fighter1: { name: bout.fighterBlue },
      fighter2: { name: bout.fighterRed },
    }));
    
    return buildBracketView(bracketSize, firstRoundMatchups, elimBouts);
  }, [bracketSize, bouts]);

  if (stages.length === 0) return null;

  return (
    <div className="mb-5">
      {stages.map(stage => (
        <div key={stage.round} className="mb-4">
          <div className="text-xs font-bold text-gray-500 mb-2">{stage.stageName}</div>
          {stage.matchups.map((m, idx) => (
            <BracketBoutCard key={idx} matchup={m} officials={officials} officialRoles={officialRoles} onBoutClick={onBoutClick} onUpdateBout={onUpdateBout} eventId={eventId} />
          ))}
        </div>
      ))}
    </div>
  );
}

function BracketBoutCard({ matchup: m, officials, officialRoles, onBoutClick, onUpdateBout, eventId }) {
  const [editingRef, setEditingRef] = useState(false);
  const [editingJudges, setEditingJudges] = useState(false);
  const [selectedJudges, setSelectedJudges] = useState([]);

  const bout = m.bout;
  const isDone = bout?.status === "confirmed";
  const isLive = bout?.status === "scoring" || bout?.status === "submitted";
  const isBye = m.fighter1?.isBye || m.fighter2?.isBye || m.fighter1?.name === "BYE" || m.fighter2?.name === "BYE";

  if (isBye) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2 opacity-70">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">{m.fighter1?.name === "BYE" ? m.fighter2?.name : m.fighter1?.name}</span>
          </div>
          <span className="text-xs text-gray-400 italic">BYE → advances</span>
        </div>
      </div>
    );
  }

  const handleRefChange = async (refName) => {
    if (bout && onUpdateBout) {
      await onUpdateBout(bout.id, { refName });
    }
    setEditingRef(false);
  };

  const handleSaveJudges = async () => {
    if (bout && onUpdateBout) {
      await onUpdateBout(bout.id, { judges: selectedJudges });
    }
    setEditingJudges(false);
  };

  const boutFighters = bout ? [bout.fighterBlue, bout.fighterRed] : [];
  const availableOfficials = (officials || []).filter(name => !boutFighters.includes(name));
  const refName = bout?.refName || "";
  const judges = bout?.judges || [];
  // A ref can't also be one of the bout's judges — same rule as pool ref/judge assignment.
  const availableJudgeOfficials = availableOfficials.filter(name => name !== refName);
  // Only referees can be assigned as a bout's ref — plain judges can't. Keep the
  // currently-assigned ref selectable even if their role changed since.
  const availableRefOfficials = availableOfficials.filter(name => ((officialRoles || {})[name] || "judge") === "referee");
  const refOptions = [...new Set(refName ? [...availableRefOfficials, refName] : availableRefOfficials)];

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2 transition-colors"
      style={{ borderColor: isDone ? "#4CAF5044" : isLive ? "#1E88E544" : "#333" }}>
      {/* Fighters + Score */}
      <button type="button" disabled={!bout || !onBoutClick} onClick={() => onBoutClick(bout)}
        aria-label={`Open bout: ${m.fighter1?.name || "TBD"} versus ${m.fighter2?.name || "TBD"}, ${isDone ? "complete" : isLive ? "live" : "pending"}`}
        className="flex items-center justify-between w-full text-left bg-transparent border-0 text-white cursor-pointer disabled:cursor-default">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display text-sm font-bold" style={{
              color: m.winner === m.fighter1?.name ? "#81C784" : m.fighter1?.name === "TBD" ? "#bbb" : "#64B5F6",
            }}>
              {m.fighter1?.name || "TBD"}
            </span>
            {m.winner === m.fighter1?.name && <span className="text-green-action-text text-xs">✓</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-bold" style={{
              color: m.winner === m.fighter2?.name ? "#81C784" : m.fighter2?.name === "TBD" ? "#bbb" : "#EF9A9A",
            }}>
              {m.fighter2?.name || "TBD"}
            </span>
            {m.winner === m.fighter2?.name && <span className="text-green-action-text text-xs">✓</span>}
          </div>
        </div>
        <div className="text-right">
          {isDone && bout?.confirmedResult && (
            <div>
              <div className="font-display text-sm font-extrabold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</div>
              <div className="font-display text-sm font-extrabold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</div>
            </div>
          )}
          {isLive && <span className="text-xs font-bold text-blue-fighter-accent">LIVE</span>}
          {bout && !isDone && (
            <div className="text-right">
              {(bout.officialCode || bout.code) && (
                <>
                  <div className="font-display text-2xl font-extrabold text-gray-200 tracking-[0.15em]">{bout.officialCode || bout.code}</div>
                  <div className="font-display text-[10px] font-bold text-gray-500 tracking-widest">OFFICIAL CODE</div>
                </>
              )}
            </div>
          )}
        </div>
      </button>
      {bout && !isDone && eventId && (bout.officialCode || bout.code) &&
        <CodeQR path={`/bout/join?code=${encodeURIComponent(bout.officialCode || bout.code)}`} label="Show official QR" />}

      {/* Ref + Judges display and assignment */}
      {bout && !isDone && (
        <div className="mt-2 pt-2 border-t border-dark-500/30">
          {/* Ref */}
          <div className="flex items-center justify-between mb-1.5">
            {!editingRef ? (
              <>
                <span className="text-sm text-gray-500">
                  {refName ? <span>Ref: <span className="text-gray-300">{refName}</span></span> : <span className="text-gray-600">No ref</span>}
                </span>
                <button onClick={(e) => { e.stopPropagation(); setEditingRef(true); }}
                  className="text-sm text-gray-500 bg-transparent border-none cursor-pointer hover:text-gray-300">
                  {refName ? "change" : "+ assign"}
                </button>
              </>
            ) : (
              <select aria-label={`Referee for ${m.fighter1?.name || "TBD"} versus ${m.fighter2?.name || "TBD"}`} value={refName} onChange={(e) => handleRefChange(e.target.value)}
                onBlur={() => setEditingRef(false)} autoFocus
                className="w-full p-2 bg-dark-800 border border-dark-500 rounded text-white text-sm outline-none"
                style={{ appearance: "auto" }}>
                <option value="">— No ref —</option>
                {[...refOptions].sort().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Judges */}
          <div className="flex items-center justify-between">
            {!editingJudges ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  {judges.length > 0 ? <><span className="text-xs text-gray-600 mr-0.5">Judges:</span>{judges.map(j => (
                    <span key={j} className="px-2 py-1 bg-dark-800 border border-dark-500 rounded text-xs text-gray-400">{j}</span>
                  ))}</> : <span className="text-sm text-gray-600">No judges</span>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); setSelectedJudges([...judges]); setEditingJudges(true); }}
                  className="text-sm text-gray-500 bg-transparent border-none cursor-pointer hover:text-gray-300 ml-2 shrink-0">
                  {judges.length > 0 ? "edit" : "+ judges"}
                </button>
              </>
            ) : (
              <div className="w-full">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {availableJudgeOfficials.sort().map(name => {
                    const isSel = selectedJudges.includes(name);
                    return (
                      <button key={name} aria-pressed={isSel} onClick={(e) => { e.stopPropagation(); isSel ? setSelectedJudges(selectedJudges.filter(j => j !== name)) : setSelectedJudges([...selectedJudges, name]); }}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer"
                        style={{ background: isSel ? "#1B5E20" : "#111", borderColor: isSel ? "#81C784" : "#666", color: isSel ? "#fff" : "#ddd" }}>
                        {name} {isSel && "✓"}
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={(e) => { e.stopPropagation(); setEditingJudges(false); }}
                    className="flex-1 p-1.5 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-xs font-bold cursor-pointer">Cancel</button>
                  <button onClick={(e) => { e.stopPropagation(); handleSaveJudges(); }}
                    className="flex-1 p-1.5 bg-green-action-light text-white rounded-lg text-xs font-bold border-none cursor-pointer">Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BracketTree — classic side-by-side tournament bracket, view-only (no ref/judge
 * assignment, no click-through). Rounds are columns of equal total height; because
 * each round has half as many matchups as the previous one, positioning every column
 * with the SAME total height and evenly-spaced (space-around-style) centers makes each
 * round r+1 match land exactly on the midpoint of the two round r matches that feed it
 * — so the elbow connectors below are exact, not approximate.
 */
// Mobile keeps the tree compact and fixed-size — it's already meant to be scrolled
// horizontally on a phone. Desktop instead fits the tree's column width to whatever
// horizontal space is actually available (measured via ResizeObserver on the
// scroll container below), so it fills a wide screen without forcing a scrollbar,
// but still shrinks gracefully on a narrower laptop instead of overflowing. Values
// are plain px because the connector math below positions everything with inline
// styles, not CSS breakpoints.
const MOBILE_DIMS = { unit: 92, boxW: 208, boxH: 72, gapW: 48, titleH: 32 };
const DESKTOP_BOX_H = 92;
const DESKTOP_TITLE_H = 38;
const DESKTOP_UNIT = 120;
const DESKTOP_GAP_W = 56;
const DESKTOP_MIN_BOX_W = 140;
const DESKTOP_MAX_BOX_W = 280;
const DESKTOP_QUERY = "(min-width: 1024px)"; // matches Tailwind's `lg`

function treeCenterY(i, n, height) {
  return (height * (i + 0.5)) / n;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

// Width of the tree's own scroll container — NOT the content's natural width. A
// block-level element with overflow-x:auto still takes its width from its parent
// (the page layout), so this stays stable as box widths change; it only moves when
// the window/layout actually resizes.
function useContainerWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure(); // set synchronously on mount — don't wait on a ResizeObserver callback for the first paint
    window.addEventListener("resize", measure);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [ref]);
  return width;
}

export function BracketTree({ bracketSize, bouts, showScores = true }) {
  const isDesktop = useIsDesktop();
  const containerRef = useRef(null);
  const containerWidth = useContainerWidth(containerRef);

  const stages = useMemo(() => {
    if (!bracketSize || !bouts) return [];
    const elimBouts = bouts.filter(b => b.eliminationRound != null);
    const firstRoundBouts = elimBouts.filter(b => b.eliminationRound === 0).sort((a, b) => a.eliminationMatch - b.eliminationMatch);
    const firstRoundMatchups = firstRoundBouts.map((bout, i) => ({
      matchIndex: i,
      fighter1: { name: bout.fighterBlue },
      fighter2: { name: bout.fighterRed },
    }));
    return buildBracketView(bracketSize, firstRoundMatchups, elimBouts);
  }, [bracketSize, bouts]);

  if (stages.length === 0) return null;

  const finalMatch = stages[stages.length - 1].matchups[0];
  const champion = finalMatch?.winner || null;

  let dims = MOBILE_DIMS;
  if (isDesktop) {
    const numCols = stages.length + (champion ? 1 : 0);
    const numGaps = (stages.length - 1) + (champion ? 1 : 0);
    const fitBoxW = containerWidth > 0 ? (containerWidth - numGaps * DESKTOP_GAP_W) / numCols : DESKTOP_MAX_BOX_W;
    const boxW = Math.max(DESKTOP_MIN_BOX_W, Math.min(DESKTOP_MAX_BOX_W, fitBoxW));
    dims = { unit: DESKTOP_UNIT, boxW, boxH: DESKTOP_BOX_H, gapW: DESKTOP_GAP_W, titleH: DESKTOP_TITLE_H };
  }

  const height = stages[0].matchups.length * dims.unit;

  return (
    <div className="mb-5 overflow-x-auto" ref={containerRef}>
      <div className="flex items-start" style={{ width: "max-content" }}>
        {stages.map((stage, r) => (
          <Fragment key={r}>
            <div className="flex flex-col items-stretch shrink-0">
              <div className={`font-bold text-gray-500 text-center tracking-wider uppercase flex items-center justify-center ${isDesktop ? "text-sm" : "text-xs"}`}
                style={{ width: dims.boxW, height: dims.titleH }}>
                {stage.stageName}
              </div>
              <div className="relative" style={{ width: dims.boxW, height }}>
                {stage.matchups.map((m, i) => (
                  <TreeMatchBox key={i} m={m} showScores={showScores} large={isDesktop}
                    style={{ position: "absolute", top: treeCenterY(i, stage.matchups.length, height) - dims.boxH / 2, left: 0, width: dims.boxW, height: dims.boxH }} />
                ))}
              </div>
            </div>
            {r < stages.length - 1 && (
              <PairConnectors n={stage.matchups.length} height={height} gapW={dims.gapW} titleH={dims.titleH} />
            )}
          </Fragment>
        ))}
        {champion && (
          <>
            <StraightConnector height={height} gapW={dims.gapW} titleH={dims.titleH} />
            <div className="flex flex-col items-stretch shrink-0">
              <div className={`font-bold tracking-wider uppercase flex items-center justify-center ${isDesktop ? "text-sm" : "text-xs"}`}
                style={{ width: dims.boxW, height: dims.titleH, color: "#FFD54F" }}>
                🏆 Champion
              </div>
              <div className="relative" style={{ width: dims.boxW, height }}>
                <div className="rounded-xl border flex items-center justify-center text-center px-3"
                  style={{
                    position: "absolute", top: height / 2 - dims.boxH / 2, left: 0, width: dims.boxW, height: dims.boxH,
                    background: "linear-gradient(135deg, #3a2f0e, #1a1608)", borderColor: "#FFD54F66",
                  }}>
                  <span className={`font-display font-extrabold ${isDesktop ? "text-xl" : "text-base"}`} style={{ color: "#FFD54F" }}>{champion}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TreeMatchBox({ m, style, showScores = true, large = false }) {
  const isBye = m.fighter1?.isBye || m.fighter2?.isBye || m.fighter1?.name === "BYE" || m.fighter2?.name === "BYE";
  const bout = m.bout;
  const nameClass = `font-semibold truncate ${large ? "text-base" : "text-sm"}`;
  const scoreClass = `font-display font-bold shrink-0 ${large ? "text-sm" : "text-xs"}`;

  if (isBye) {
    const advancing = m.fighter1?.name === "BYE" ? m.fighter2?.name : m.fighter1?.name;
    return (
      <div style={style} className={`rounded-xl border border-dark-500 bg-dark-700 opacity-70 flex items-center ${large ? "px-4" : "px-3"}`}>
        <span className={nameClass + " text-white"}>{advancing} <span className="text-gray-400 font-normal">— BYE</span></span>
      </div>
    );
  }

  const statusColor = { waiting: "#bbb", scoring: "#64B5F6", submitted: "#FFB300", confirmed: "#81C784" }[bout?.status] || "#bbb";

  return (
    <div style={style} className={`rounded-xl border border-dark-500 bg-dark-700 flex flex-col justify-center gap-1 ${large ? "px-4 py-2" : "px-3 py-1.5"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={nameClass} style={{ color: m.winner === m.fighter1?.name ? "#81C784" : m.fighter1?.name === "TBD" ? "#bbb" : "#64B5F6" }}>
          {m.fighter1?.name || "TBD"}
        </span>
        {showScores && bout?.confirmedResult && <span className={scoreClass} style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>}
      </div>
      <div className="h-px bg-dark-500/50" />
      <div className="flex items-center justify-between gap-2">
        <span className={nameClass} style={{ color: m.winner === m.fighter2?.name ? "#81C784" : m.fighter2?.name === "TBD" ? "#bbb" : "#EF9A9A" }}>
          {m.fighter2?.name || "TBD"}
        </span>
        {showScores && bout?.confirmedResult && <span className={scoreClass} style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>}
      </div>
      {bout && !bout.confirmedResult && (
        <div className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full" style={{ background: statusColor }} />
      )}
    </div>
  );
}

// Elbow connectors linking each pair of adjacent matches in a round to the single
// match they feed into next round (see BracketTree doc comment for why the midpoint
// math lines up exactly).
function PairConnectors({ n, height, gapW, titleH }) {
  const pairs = [];
  for (let i = 0; i < n; i += 2) pairs.push([i, i + 1]);
  return (
    <div className="relative shrink-0" style={{ width: gapW, height, marginTop: titleH }}>
      {pairs.map(([a, b], idx) => {
        const y1 = treeCenterY(a, n, height);
        const y2 = treeCenterY(b, n, height);
        const yMid = (y1 + y2) / 2;
        const top = Math.min(y1, y2);
        const lineHeight = Math.abs(y2 - y1);
        return (
          <Fragment key={idx}>
            <div style={{ position: "absolute", left: 0, top: y1, width: gapW / 2, height: 2, background: "#333" }} />
            <div style={{ position: "absolute", left: 0, top: y2, width: gapW / 2, height: 2, background: "#333" }} />
            <div style={{ position: "absolute", left: gapW / 2, top, width: 2, height: lineHeight, background: "#333" }} />
            <div style={{ position: "absolute", left: gapW / 2, top: yMid, width: gapW / 2, height: 2, background: "#333" }} />
          </Fragment>
        );
      })}
    </div>
  );
}

function StraightConnector({ height, gapW, titleH }) {
  return (
    <div className="relative shrink-0" style={{ width: gapW, height, marginTop: titleH }}>
      <div style={{ position: "absolute", left: 0, top: height / 2, width: gapW, height: 2, background: "#FFD54F66" }} />
    </div>
  );
}
